import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import * as React from "react";

import { useAuth, useNavigation } from "@/providers";
import { useCurrentOrgId } from "@/providers/auth/hooks";
import { useNotification } from "@/shared/components/notifications/useNotification";
import { CollectionResourceType } from "@/shared/domain/collections";
import { useCreateCollection } from "@/shared/domain/collections/hooks/useCreateCollection";
import { FileNode } from "@/shared/domain/files/filesystem";

interface CreateCollectionProps {
  selectedDatasets?: Set<string>;
  selectedFiles?: FileNode[];
  selectedFileIds?: Set<string>;
  selectedEvents?: Set<string>;
  close: () => void;
}

export const CreateCollection: React.FC<CreateCollectionProps> = ({
  selectedDatasets,
  selectedFiles,
  selectedFileIds,
  selectedEvents,
  close,
}) => {
  let derivedType: CollectionResourceType = "file";
  if (selectedDatasets?.size) {
    derivedType = "dataset";
  }
  if (selectedFiles?.length || selectedFileIds?.size) {
    derivedType = "file";
  }
  if (selectedEvents?.size) {
    derivedType = "event";
  }

  const [newCollectionName, setNewCollectionName] = React.useState<string>("");
  const [newCollectionDesc, setNewCollectionDesc] = React.useState<string>("");
  const [resourceType, setResourceType] =
    React.useState<CollectionResourceType>(derivedType);

  const { currentOrganization } = useAuth();
  const orgId = useCurrentOrgId(currentOrganization?.org_id);
  const { goto } = useNavigation();
  const showNotification = useNotification();
  const {
    mutate: createCollection,
    isPending,
    error,
    reset,
  } = useCreateCollection();

  const handleCreate = () => {
    if (!newCollectionName) {
      return;
    }

    const datasets =
      selectedDatasets &&
      Array.from(selectedDatasets).map((id) => ({
        resource_type: "dataset" as const,
        resource_id: id,
      }));

    const files =
      selectedFiles &&
      selectedFiles.map((item) => ({
        resource_type: "file" as const,
        resource_id: item.file?.file_id ?? "",
      }));

    const fileIds =
      selectedFileIds &&
      Array.from(selectedFileIds).map((id) => ({
        resource_type: "file" as const,
        resource_id: id,
      }));

    const events =
      selectedEvents &&
      Array.from(selectedEvents).map((id) => ({
        resource_type: "event" as const,
        resource_id: id,
      }));

    const resources = events ?? fileIds ?? files ?? datasets ?? undefined;

    createCollection(
      {
        orgId,
        name: newCollectionName,
        description: newCollectionDesc || undefined,
        resourceType,
        resources,
      },
      {
        onSuccess: (record) => {
          close();
          if (!selectedDatasets) {
            goto.collection({ collectionId: record.collection_id });
          } else {
            showNotification("Collection created");
          }
        },
      },
    );
  };

  let resourceCountLabel: string | null = null;
  if (selectedEvents?.size) {
    resourceCountLabel = `(${selectedEvents.size})`;
  } else if (selectedFileIds?.size) {
    resourceCountLabel = `(${selectedFileIds.size})`;
  } else if (selectedFiles?.length) {
    resourceCountLabel = `(${selectedFiles.length})`;
  } else if (selectedDatasets?.size) {
    resourceCountLabel = `(${selectedDatasets.size})`;
  }

  return (
    <Box
      component="form"
      onSubmit={(e) => {
        e.preventDefault();
        handleCreate();
      }}
      sx={{
        display: "flex",
        gap: "1.25rem",
        flexDirection: "column",
        justifyContent: "center",
        padding: "0.5rem",
      }}
    >
      {error && (
        <Alert severity="error" onClose={reset}>
          {error.message}
        </Alert>
      )}
      <TextField
        autoFocus
        size="small"
        id="collection-name-text"
        placeholder="Name"
        variant="outlined"
        inputProps={{
          autoComplete: "off",
        }}
        value={newCollectionName}
        onChange={(e) => setNewCollectionName(e.target.value)}
      />
      <TextField
        size="small"
        id="collection-description-text"
        placeholder="Description (optional)"
        multiline
        minRows={2}
        maxRows={5}
        inputProps={{
          autoComplete: "off",
        }}
        variant="outlined"
        value={newCollectionDesc}
        onChange={(e) => setNewCollectionDesc(e.target.value)}
      />
      <FormControl size="small" fullWidth>
        <InputLabel id="collection-resource-type-label">Entity Type</InputLabel>
        <Select
          labelId="collection-resource-type-label"
          id="collection-resource-type"
          value={resourceType}
          label="Entity Type"
          onChange={(e) =>
            setResourceType(e.target.value as CollectionResourceType)
          }
        >
          <MenuItem value="file">File</MenuItem>
          <MenuItem value="dataset">Dataset</MenuItem>
          <MenuItem value="event">Event</MenuItem>
        </Select>
      </FormControl>
      <DialogActions sx={{ justifyContent: "center" }}>
        <LoadingButton
          loading={isPending}
          type="submit"
          disabled={!newCollectionName}
          variant="outlined"
          style={{
            textTransform: "none",
          }}
          size="small"
        >
          Create Collection {resourceCountLabel}
        </LoadingButton>
      </DialogActions>
    </Box>
  );
};
